import {
    generateEventId,
    getAttributionIDFromLocalStorage,
    sendEventToCourier,
} from '@eventbrite/clid-courier';
import grylls from '@eventbrite/grylls';
import * as Sentry from '@sentry/browser';
import { Dispatch } from 'redux';
import { CHECKOUT_FORM_NAME } from '../constants';
import { OrderAttendee } from '../models/attendee';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';

/**
 * Safe wrapper for grylls.track() to prevent checkout failures
 * Adds null checks and try-catch to handle cases where grylls is unavailable
 */
export const safeGryllsTrack = (action: string, properties?: any) => {
    try {
        if (!grylls || typeof grylls.track !== 'function') {
            // This should not be sent to Sentry
            console.warn('Grylls track not available', { action });
            return;
        }
        grylls.track(action, properties);
    } catch (error) {
        const context = {
            extra: {
                originalError: error,
                action,
                properties,
            },
        };
        Sentry.captureException('PixelAnalytics: Grylls track failed', context);
        console.error('PixelAnalytics: Grylls track failed', context);
    }
};

/**
 * Safe wrapper for grylls.load() to prevent checkout failures
 * Adds null checks and try-catch to handle cases where grylls is unavailable
 */
export const safeGryllsLoad = (trackingData: any, options: any) => {
    try {
        if (!grylls || typeof grylls.load !== 'function') {
            console.warn('Grylls load not available');
            return;
        }
        grylls.load(trackingData, options);
    } catch (error) {
        console.error('Grylls load failed', {
            error: error instanceof Error ? error.message : String(error),
        });
        // Fail silently as per requirements
    }
};

/**
 * Safe check for grylls initialization status
 * Adds null checks to prevent errors when grylls.analyticsjs is unavailable
 */
export const safeGryllsInitialized = () => {
    try {
        return !!(
            grylls &&
            grylls.analyticsjs &&
            grylls.analyticsjs.initialized
        );
    } catch (error) {
        console.warn('Unable to check grylls initialization status', {
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
};

export enum GryllsEventbriteEvent {
    // triggered when embed loads on external website
    listing = 'event_listing',
    // triggered on submit of selected tickets
    register = 'event_register',
    // triggered at order confirmation page load
    orderconfirmation = 'event_order_confirmation',
}

export enum GryllsAction {
    // Trigger Actions from /myevent/<EID>/tracking
    completeRegistration = 'CompleteRegistration',
    pickASeat = 'pick_a_seat',
    viewContent = 'ViewContent',
    initiateCheckout = 'InitiateCheckout',
    addToCart = 'AddToCart',
    addPaymentInfo = 'AddPaymentInfo',
}

// Trigger Actions from /myevent/<EID>/tracking
/**
 * @deprecated Use `GryllsAction.viewContent` instead
 */
export const VIEW_CONTENT = GryllsAction.viewContent;
/**
 * @deprecated Use `GryllsAction.addToCart` instead
 */
export const ADD_TO_CART = GryllsAction.addToCart;
/**
 * @deprecated Use `GryllsAction.initiateCheckout` instead
 */
export const INITIATE_CHECKOUT = GryllsAction.initiateCheckout;
/**
 * @deprecated Use `GryllsAction.addPaymentInfo` instead
 */
export const ADD_PAYMENT_INFO = GryllsAction.addPaymentInfo;
/**
 * @deprecated Use `GryllsAction.completeRegistration` instead
 */
export const COMPLETE_REGISTRATION = GryllsAction.completeRegistration;
/**
 * @deprecated Use `GryllsAction.pickASeat` instead
 */
export const PICK_A_SEAT = GryllsAction.pickASeat;

/**
 * @deprecated Use `GryllsEventbriteEvent.listing` instead
 */
export const EVENT_LISTING = GryllsEventbriteEvent.listing;

/**
 * @deprecated Use `GryllsEventbriteEvent.register` instead
 */
export const EVENT_REGISTER = GryllsEventbriteEvent.register;

/**
 * @deprecated Use `GryllsEventbriteEvent.orderconfirmation` instead
 */
export const EVENT_ORDER_CONFIRMATION = GryllsEventbriteEvent.orderconfirmation;

// grylls-specific action
export const COMPLETED_ORDER = 'Completed Order';

export const MAP_TRIGGER_ACTION_TO_EB_EVENT = {
    [GryllsAction.viewContent]: GryllsEventbriteEvent.listing,
    [GryllsAction.initiateCheckout]: GryllsEventbriteEvent.register,
    [GryllsAction.completeRegistration]:
        GryllsEventbriteEvent.orderconfirmation,
};

// Pixel Tracker type
export const FACEBOOK_PIXEL = 'Facebook Pixel';
export const GOOGLE_ANALAYTICS_PIXEL = 'Google Analytics';

// derive purchased quantities of each individual ticketClassId
const _getTicketQuantity = (
    attendees: Array<OrderAttendee>,
    ticketClassId: string,
) => {
    const ticketQuantity = 0;

    const attendeesByTicketClassId = attendees.filter(
        (attendee) => attendee.ticketClassId === ticketClassId,
    );

    return attendeesByTicketClassId.reduce(
        (accumulator, currentValue) => accumulator + currentValue.quantity,
        ticketQuantity,
    );
};

const _attendeeClassIdMap = (attendees: Array<OrderAttendee>) =>
    attendees.map((mapObj) => mapObj.ticketClassId);

// create ticket groupings for individual ticketClassIds
const _createTicketGroups = (attendees: Array<OrderAttendee>) => {
    const attendeeClassIdMap = _attendeeClassIdMap(attendees);

    return attendees.filter(
        (attendee, pos) =>
            attendeeClassIdMap.indexOf(attendee.ticketClassId) === pos,
    );
};

/**
 * _getTrackPropertiesForOrder is an internal helper called by
 * _getTrackProperties. It returns data derived from the user's
 * current order suitable for input to calls to grylls.track.
 *
 * @param order is our internal representation of a user's order from the state
 * store.
 */
const _getTrackPropertiesForOrder = (
    order: CheckoutState['order'],
    eventName: string,
) => {
    if (!('costs' in order)) {
        return {};
    }

    const {
        costs: {
            gross: { majorValue, currency },
        },
        attendees = [],
    } = order;

    const products = _createTicketGroups(attendees).map((attendee) => {
        const {
            costs: {
                gross: { majorValue: price, currency: ticketCurrency },
            },
            ticketClassId: id,
            ticketClassId: sku,
        } = attendee;

        return {
            name: eventName,
            id,
            sku,
            quantity: _getTicketQuantity(attendees, sku),
            price,
            currency: ticketCurrency,
        };
    });

    return {
        products,
        revenue: majorValue,
        currency,
    };
};

/**
 * _getTrackProperties returns an object formatted as the desired
 * input to calls to grylls.track.
 *
 * @param event is our internal representation of an event from the state store.
 * @param order is our internal representation of a user's order from the state
 * store.
 */
const _getTrackProperties = (
    event: EventNamespace,
    order: CheckoutState['order'],
    gryllsAction: GryllsEventbriteEvent | GryllsAction.pickASeat,
) => {
    const {
        id: eventId,
        name: { text: eventName },
    } = event;
    const productProperties = _getTrackPropertiesForOrder(order, eventName);

    const trackProperties: Record<
        GryllsEventbriteEvent | GryllsAction.pickASeat,
        unknown
    > = {
        [GryllsEventbriteEvent.listing]: {
            eventId,
        },
        [GryllsEventbriteEvent.register]: productProperties,
        [GryllsEventbriteEvent.orderconfirmation]: productProperties,
        [GryllsAction.pickASeat]: {
            eventId,
        },
    };

    return trackProperties[gryllsAction];
};

export const triggerTrackingPixelAction =
    (triggerAction: GryllsAction) =>
    (_dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const { order, event, app, user, form } = getState();
        const { gryllsTrackingData } = app;

        if (triggerAction === GryllsAction.pickASeat && 'id' in event) {
            const gryllsAction = GryllsAction.pickASeat;
            const trackProperties = _getTrackProperties(
                event,
                order,
                gryllsAction,
            );

            const stableId = getAttributionIDFromLocalStorage();

            // Generate synthetic event ID for Facebook deduplication
            const syntheticFbEventId = stableId
                ? generateEventId(stableId)
                : null;

            // Add syntheticFbEventId to trackProperties for Grylls
            const trackPropertiesWithSyntheticId = {
                ...(trackProperties as Record<string, any>),
                syntheticFbEventId: syntheticFbEventId,
            };

            safeGryllsTrack(gryllsAction, trackPropertiesWithSyntheticId);

            if (syntheticFbEventId && stableId && event?.organization?.id) {
                sendEventToCourier(
                    gryllsAction, // pick_a_seat
                    syntheticFbEventId,
                    stableId,
                    event.organization.id,
                    gryllsTrackingData,
                    trackProperties,
                );
            }
        }

        // Build the properties to pass along for tracking from the order data
        if (
            triggerAction === GryllsAction.completeRegistration &&
            'id' in order &&
            'id' in event
        ) {
            const {
                id: orderId,
                costs: {
                    gross: { majorValue, currency },
                },
                attendees,
            } = order;
            const {
                name: { text: eventName },
            } = event;
            const products = _createTicketGroups(attendees).map((attendee) => {
                const {
                    costs: {
                        gross: { majorValue: price, currency: ticketCurrency },
                    },
                    /* GA expects a sku (Stock Keeping Unit), a unit often associated with retail items
                     * In our case, ticketClassId most closely corresponds with the definition of a sku
                     */
                    ticketClassId: sku,
                } = attendee;

                return {
                    name: eventName,
                    sku,
                    quantity: _getTicketQuantity(attendees, sku),
                    price,
                    currency: ticketCurrency,
                };
            });

            const trackingData = {
                orderId,
                affiliation: eventName,
                total: majorValue,
                revenue: majorValue,
                currency,
                products,
            };

            // Track `Completed Order` no matter what is custom configured
            // Completed Order is a specific grylls e-commerce named event
            // For Facebook, it maps to 'Purchase' in grylls library
            safeGryllsTrack(COMPLETED_ORDER, trackingData);

            const stableId = getAttributionIDFromLocalStorage();

            if (stableId && event?.organization?.id) {
                // Get email: user email if available, fallback to form email
                const userEmail = user?.email;
                const formEmail =
                    form?.[CHECKOUT_FORM_NAME]?.values?.buyer?.['N-email'];
                const email = userEmail || formEmail;

                const userData = {
                    email,
                    firstName:
                        form?.[CHECKOUT_FORM_NAME]?.values?.buyer?.[
                            'N-first_name'
                        ],
                    lastName:
                        form?.[CHECKOUT_FORM_NAME]?.values?.buyer?.[
                            'N-last_name'
                        ],
                    zipCode:
                        form?.[CHECKOUT_FORM_NAME]?.values?.buyer?.[
                            'N-zip_code_tax_residence'
                        ],
                };

                sendEventToCourier(
                    'event_order_confirmation',
                    orderId.toString(),
                    stableId,
                    event.organization.id,
                    gryllsTrackingData,
                    trackingData,
                    userData,
                );
            }

            /* eslint-disable */
            // EB-113842 AdRoll collect dynamic conversion values
            (window as any).adroll_conversion_value = majorValue;
            (window as any).adroll_currency = currency;
            /* eslint-enable */

            // Track via EB GA account
            if (typeof window.ga === 'function') {
                const ga = window.ga;

                ga('require', 'ecommerce');

                let gaTxn = {
                    id: orderId.toString(),
                    affiliation: eventName,
                    revenue: majorValue,
                    currency,
                };

                // Check wether we need to track this purchase on GA for Youtube integration
                const localStorageKey = 'eb_youtube_events';
                const gaEventId = event.id;
                let eventsToLogString;
                let eventsToLog = [];
                let youtubeParams = {};

                try {
                    eventsToLogString = localStorage.getItem(localStorageKey);
                } catch (err) {
                    // No access to local storage, do nothing.
                }

                if (eventsToLogString) {
                    eventsToLog = JSON.parse(eventsToLogString);
                }
                // If this event was previously accessed through Youtube, track it and delete it for future purchases
                const gaEventIdIndex = eventsToLog.indexOf(gaEventId);

                if (gaEventIdIndex > -1) {
                    youtubeParams = {
                        dimension36: 'true',
                        dimension37: gaEventId,
                    };
                    eventsToLog.splice(gaEventIdIndex, 1);

                    if (eventsToLog.length) {
                        localStorage.setItem(
                            localStorageKey,
                            JSON.stringify(eventsToLog),
                        );
                    } else {
                        localStorage.removeItem(localStorageKey);
                    }
                    gaTxn = {
                        ...gaTxn,
                        ...youtubeParams,
                    };
                }

                ga('ecommerce:addTransaction', gaTxn);
                products.forEach((product) => {
                    const { sku, name, quantity, price } = product;

                    const gaItem = {
                        id: orderId.toString(),
                        sku,
                        name,
                        category: event.terminology?.ticket || 'Ticket',
                        price: price.toString(),
                        quantity: quantity.toString(),
                        currency,
                        ...youtubeParams,
                    };

                    ga('ecommerce:addItem', gaItem);
                });

                ga('ecommerce:send');
            }
        }

        // For triggers specific to checkout widget flow, track the corresponding event
        // NOTE: also disregards any custom configuration in favor of standardization
        if (triggerAction in MAP_TRIGGER_ACTION_TO_EB_EVENT && 'id' in event) {
            const gryllsAction =
                MAP_TRIGGER_ACTION_TO_EB_EVENT[
                    triggerAction as keyof typeof MAP_TRIGGER_ACTION_TO_EB_EVENT
                ];
            const trackProperties = _getTrackProperties(
                event,
                order,
                gryllsAction,
            );

            const stableId = getAttributionIDFromLocalStorage();

            // Generate synthetic event ID for Facebook deduplication
            const syntheticFbEventId = stableId
                ? generateEventId(stableId)
                : null;

            // Add syntheticFbEventId to trackProperties for Grylls
            const trackPropertiesWithSyntheticId = {
                ...(trackProperties as Record<string, any>),
                syntheticFbEventId: syntheticFbEventId,
            };

            safeGryllsTrack(gryllsAction, trackPropertiesWithSyntheticId);

            // We don't want to send the completed order event to attribution-service here, it's handled separately above
            if (
                syntheticFbEventId &&
                stableId &&
                event?.organization?.id &&
                gryllsAction !== 'event_order_confirmation'
            ) {
                sendEventToCourier(
                    gryllsAction, // event_listing, pick_a_seat, event_register
                    syntheticFbEventId,
                    stableId,
                    event.organization.id,
                    gryllsTrackingData,
                    trackProperties,
                );
            }
        }
    };

export const initializeGrylls =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const {
            app: {
                gryllsTrackingData,
                gryllsOptions,
                isEventbriteTLD,
                featureFlags: { launchSupportMultipleFacebookPixels },
            },
        } = getState();

        const gryllsInitialized = safeGryllsInitialized();

        gryllsOptions.launchSupportMultipleFacebookPixels =
            launchSupportMultipleFacebookPixels;

        if (!gryllsInitialized) {
            safeGryllsLoad(gryllsTrackingData, gryllsOptions);

            // Don't fire the pixels if the embed is loading on an Eventbrite
            // domain, this will cause double firing of the pixel.
            if (!isEventbriteTLD) {
                dispatch(triggerTrackingPixelAction(GryllsAction.viewContent));
            }
        }
    };
